/**
 * VideoVariantsTabContent — Variants tab content for the Video Generator bottom panel.
 *
 * Contains: video history grid with selection, pending spinners, empty state.
 * Each cell renders at the aspect ratio of its source variant (read from
 * BasicAssetMetadata.aspect_ratio when present), falling back to the node's
 * default aspect ratio so the variant preview always matches its underlying.
 */

import { memo, useState, useRef, useEffect, useMemo, useCallback, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import { getVariantLabel, type VideoHistoryEntry } from './VideoHistoryPopover'
import { VideoPreviewPlayer } from './VideoPreviewPlayer'
import { ASPECT_RATIO_MAP } from './constants'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useVariantMarkers } from '../../../../hooks/useVariantMarkers'
import { VariantMarkerButton } from './VariantMarkerButton'
import { VariantsGridSkeleton } from './VariantsGridSkeleton'
import {
  VariantTabChips,
  VARIANT_TAB_ROW_HEIGHT,
} from './VariantTabChips'
import {
  countVariantTabs,
  DEFAULT_VARIANT_TAB,
  findOrphanMarkerKeys,
  getVariantKey,
  isArchivedMarker,
  selectVariantsForTab,
  type VariantTab,
} from './variantKey'

/**
 * Parse a CSS aspect-ratio string (e.g., "16/9") into a numeric value (width / height).
 * Returns 16/9 for invalid/missing input as a safe default.
 */
function parseAspectRatio(ar: string): number {
  const parts = ar.split('/')
  const w = Number(parts[0])
  const h = Number(parts[1])
  if (!w || !h || w <= 0 || h <= 0) return 16 / 9
  return w / h
}

/**
 * Normalize a stored aspect_ratio value into CSS aspect-ratio string form.
 * Accepts schema label form ("9:16") or already-CSS form ("9/16").
 * Returns null when the value is missing or unparseable so callers can
 * cleanly fall back to a node-level default.
 */
function toCssAspect(value: string | undefined | null): string | null {
  if (!value) return null
  // Schema label form: "9:16" -> "9/16" via the canonical map
  if (ASPECT_RATIO_MAP[value]) return ASPECT_RATIO_MAP[value]
  // CSS form already: "9/16"
  if (/^\d+\s*\/\s*\d+$/.test(value)) return value.replace(/\s/g, '')
  return null
}

const variantDebug = createNadDebug('dw:video-variant-aspect')
const nullHistoryDebug = createNadDebug('dw:video-variant-null-result:history')
// Hover-transition trace for the version-label placement (originally the
// version-label/play-pause overlap fix, T277018231; the label has since moved
// to the top-left corner and stays visible). Emits only on hover-state flips —
// never per playback frame.
const labelOverlapDebug = createNadDebug('dw:video-variant-label:overlap')
const galleryDebug = createNadDebug('dw:video-variant-gallery')
const filterDebug = createNadDebug('dw:variant-marker:filter')
// Module-level set to dedupe one-shot render traces across StrictMode double mounts.
const SEEN_RENDER_KEYS = new Set<string>()
const SEEN_NULL_HISTORY_KEYS = new Set<string>()
const SEEN_FILTER_KEYS = new Set<string>()

/**
 * React key for a video variant cell.
 *
 * Distinct from RFC-1146's `getVariantKey`, which is the MARKER identity: this
 * one keeps an `idx-` fallback because React keys merely need per-render
 * uniqueness, whereas a marker key must be durably tied to one asset (an index
 * fallback would silently re-attach a marker to a different clip after a
 * delete or history-cap eviction). Marker lookups below use `getVariantKey`.
 */
function getVideoVariantKey(
  entry: NonNullable<VideoHistoryEntry>,
  realIdx: number,
): string {
  const entryResult = entry.result
  const id = entryResult?.id
  const localPath = entryResult?.local_path
  if (id && localPath) return `${id}|${localPath}`
  return id || localPath || `idx-${realIdx}`
}

/**
 * Compute dynamic height for the Variants tab content area.
 * 1-2 variants -> 1 row; 3-4 -> 2 rows; 5+ -> 7 rows (with scroll).
 *
 * Each cell is rendered into a square footprint (column-width × column-width)
 * so portrait and landscape variants share the same grid bounding box; the
 * actual video shape is centered within. Row height therefore equals column
 * width regardless of variant aspect.
 *
 * Note: this function currently has no production caller — VideoGeneratorNode
 * hardcodes `contentHeight={520}` for the panel — so the grid scrolls inside
 * the existing 520px area. The helper is forward-work for dynamic-height
 * callers and is exercised by the sprint-2 unit tests.
 *
 * Canvas UI 2.0: the Variants/Archived chip row is reserved on top.
 *
 * KNOWN LIMITATION — `variantCount` is the total across BOTH tabs, because tab
 * state lives inside the component and no caller can read it. When the active
 * tab holds only some of them the panel over-reserves and leaves a gap.
 * Over-reserving is the safe side of the divergence this file's header warns
 * about (post 1533007285123236); under-reserving would clip.
 *
 * @param variantCount Tile total across both tabs (history + pending); see above.
 * @param nodeWidth Node preview width in px.
 * @param options.includeTabRow Reserve the Variants/Archived chip row (default true).
 */
export function getVideoVariantsHeight(
  variantCount: number,
  nodeWidth: number = 400,
  options: { includeTabRow?: boolean } = {},
): number {
  // Empty state renders no chip row, so it keeps the bare 150px.
  if (variantCount === 0) return 150

  // Panel is calc(100% + 120px) wide; min 632px
  const panelWidth = Math.max(nodeWidth + 120, 632)
  // p-3 = 24px total horizontal padding; gap-2 = 8px; 2 columns
  const cellWidth = (panelWidth - 24 - 8) / 2
  // Square footprint — the cell is a column-width × column-width box.
  const cellHeight = cellWidth
  const gap = 8
  const padding = 24
  const tabRowHeight =
    (options.includeTabRow ?? true) ? VARIANT_TAB_ROW_HEIGHT : 0
  const chrome = padding + tabRowHeight

  const rows = Math.ceil(variantCount / 2)

  if (rows <= 2) {
    return Math.round(rows * cellHeight + (rows - 1) * gap + chrome)
  }

  // 5+ variants: cap at 7 rows with scroll
  return Math.round(7 * cellHeight + 6 * gap + chrome)
}

/**
 * Read the per-entry aspect ratio (CSS form) from a history entry's
 * BasicAssetMetadata, falling back to the node-level default.
 */
function getEntryAspect(entry: VideoHistoryEntry, defaultAspect: string): string {
  return toCssAspect(entry?.result?.metadata?.aspect_ratio) ?? defaultAspect
}

/** Extreme portrait aspects below this are clamped to '3/4' when clamping is on. */
const PORTRAIT_ASPECT_CLAMP = 3 / 4

/**
 * Resolve the grid base aspect for the video variants grid — the SINGLE source
 * of truth shared by the rendered grid and the dedicated-panel layout sizing
 * (post 1533007285123236). Mirrors the component's rule: latest VISIBLE history
 * entry's aspect, else the node default.
 *
 * When `clampPortrait` is true (legacy shared-panel behavior) extreme portrait
 * (< 3:4) is clamped to '3/4' so a row fits within the height-constrained panel.
 * The dedicated Variants panel passes `clampPortrait: false` so 9:16 tiles render
 * true-aspect and match the original preview. Callers MUST pass the same
 * `clampPortrait` to both this helper and `<VideoVariantsTabContent>` so the
 * panel size and the rendered grid never diverge.
 */
export function resolveVideoGridAspect(
  history: VideoHistoryEntry[],
  defaultAspectRatio: string,
  options: { clampPortrait?: boolean } = {},
): string {
  const clampPortrait = options.clampPortrait ?? true
  const visible = history.filter(
    (entry): entry is NonNullable<VideoHistoryEntry> =>
      entry != null && entry.result != null,
  )
  const latest = visible[visible.length - 1]
  const rawGridAspect = latest
    ? getEntryAspect(latest, defaultAspectRatio)
    : defaultAspectRatio
  if (!clampPortrait) return rawGridAspect
  return parseAspectRatio(rawGridAspect) < PORTRAIT_ASPECT_CLAMP ? '3/4' : rawGridAspect
}

type VariantSelectionSource = 'tile' | 'player-control'

interface VideoHistoryCellProps {
  entry: NonNullable<VideoHistoryEntry>
  realIdx: number
  label: string
  isSelected: boolean
  /**
   * Per-entry aspect ratio of this video — informational only (used for the
   * NAD render trace). Cell rendering uses `gridAspect` so the whole grid
   * looks uniform.
   */
  cellAspect: string
  /**
   * The grid's base aspect ratio. ALL cells render at this aspect — for a
   * homogeneous grid it equals every entry's own aspect; for a mixed-aspect
   * grid it equals the latest variant's aspect (the user's "base" choice).
   * 16:9 source content in a 9:16 cell is cropped via the inner video's
   * `object-cover`, mirroring how the main preview crops to fit.
   */
  gridAspect: string
  defaultAspect: string
  nodeId?: string
  buildVideoUrl: (localPath: string) => string | null
  buildPosterUrl: (localPath: string) => string | null
  onSelect: (index: number, source: VariantSelectionSource) => void
  onExpand: (videoUrl: string) => void
  onDownload?: (localPath: string, realIdx: number) => void
  /** Whether this clip is archived, read from the LIVE map. */
  archived?: boolean
  /** Archive/restore this clip. Omitted when the clip is unmarkable. */
  onToggleArchived?: (archived: boolean) => void
  /** RFC-1146 exposure gate — false hides the star entirely. */
  showMarker?: boolean
}

const VideoHistoryCell = memo(function VideoHistoryCell(
  { entry, realIdx, label, isSelected, cellAspect, gridAspect, defaultAspect, nodeId, buildVideoUrl, buildPosterUrl, onSelect, onExpand, onDownload, archived = false, onToggleArchived, showMarker = true }: VideoHistoryCellProps,
) {
  const [isHovering, setIsHovering] = useState(false)
  const entryResult = entry.result
  const videoPath = entryResult?.local_path
  const videoUrl = videoPath ? buildVideoUrl(videoPath) : null
  const posterUrl = videoPath ? buildPosterUrl(videoPath) : null
  const variantId = entryResult?.id || `idx-${realIdx}`
  const isPerEntry = cellAspect !== defaultAspect

  // One-shot render trace (deduped by module-level set so StrictMode
  // double-mount does not double-emit). Includes both per-entry aspect
  // and the grid's chosen base aspect so a mixed-aspect grid is debuggable.
  useEffect(() => {
    const key = `${nodeId ?? 'unknown'}|${variantId}|${cellAspect}|${gridAspect}`
    if (SEEN_RENDER_KEYS.has(key)) return
    SEEN_RENDER_KEYS.add(key)
    variantDebug({
      event: 'video-variant:render',
      nodeId: nodeId ?? null,
      variantIndex: realIdx,
      variantId,
      entryAspectRatio: cellAspect,
      gridAspectRatio: gridAspect,
      source: isPerEntry ? 'per-entry' : 'node-level',
    })
  }, [nodeId, variantId, realIdx, cellAspect, gridAspect, isPerEntry])

  // Trace version-label visibility across hover flips. The label now lives in
  // the top-left corner, clear of the bottom control bar, so it no longer has
  // to hide while the controls are up (the T277018231 workaround). Emitting on
  // hover changes keeps that verifiable from NAD logs: `labelVisible` must stay
  // true on mouse-enter, which is what playback used to break.
  useEffect(() => {
    labelOverlapDebug({
      event: 'video-variant:label-visibility',
      nodeId: nodeId ?? null,
      variantIndex: realIdx,
      variantId,
      isHovering,
      placement: 'top-left',
      labelVisible: true,
    })
  }, [isHovering, nodeId, realIdx, variantId])

  // Every cell renders at the grid's base aspect (uniform shape across the
  // grid). Width fills the grid track; height derives from aspect-ratio.
  // Per-entry source aspect doesn't change the cell shape — VideoPreviewPlayer's
  // object-cover handles the source/cell aspect mismatch by cropping (matching
  // the main preview's behavior).
  const outerStyle: CSSProperties = {
    aspectRatio: gridAspect,
    width: '100%',
  }
  const innerStyle: CSSProperties = {
    aspectRatio: gridAspect,
    width: '100%',
    height: '100%',
  }

  // Built once, placed TOP-RIGHT either way: at the trailing end of the
  // player's overlay cluster when there is a clip, or as a plain corner box on
  // a placeholder cell, which has no cluster to join.
  const markerButton = showMarker ? (
    <VariantMarkerButton
      archived={archived}
      onToggle={(nextArchived) => onToggleArchived?.(nextArchived)}
      disabled={onToggleArchived === undefined}
      variantIndex={realIdx}
    />
  ) : null

  // A portrait tile is too narrow for mute + download + star in a row, so the
  // cluster stacks down the right edge instead.
  const overlayDirection = parseAspectRatio(gridAspect) < 1 ? 'column' : 'row'

  return (
    <div
      data-testid={`video-variant-item-${realIdx}`}
      className={cn(
        'relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer group',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 p-0.5'
          : 'border-transparent hover:border-border',
      )}
      style={outerStyle}
      onClick={() => onSelect(realIdx, 'tile')}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        data-testid={`video-variant-aspect-box-${realIdx}`}
        className="rounded-md overflow-hidden"
        style={innerStyle}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            videoUrl={videoUrl}
            posterUrl={posterUrl}
            isHovering={isHovering}
            preload="none"
            showExpand={true}
            showDownload={true}
            onExpand={() => {
              if (videoUrl) onExpand(videoUrl)
            }}
            onDownload={() => {
              if (videoPath && onDownload) {
                onDownload(videoPath, realIdx)
              }
            }}
            onPreviewSelect={() => onSelect(realIdx, 'player-control')}
            topTrailingControls={markerButton}
            overlayDirection={overlayDirection}
            toggleOnFrameClick
          />
        ) : (
          <div className="w-full h-full bg-surface-2 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-text-muted/30"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>
      {/* Version label (v1 / v2 / v1 remix 1 …) — TOP-LEFT, matching the image
          variant grid so the version number sits in the same corner on every
          node type.

          It used to share the cell's bottom edge with VideoPreviewPlayer's
          hover transport row (play/pause, scrub, timers), which spans the full
          width, so it had to hover-gate itself out of the way (T277018231) and
          was therefore invisible for the whole time a clip was playing. The
          top-left corner is clear of that row and of the media controls and
          star top-right, so the label can now stay visible at all times —
          including during playback. */}
      {/* Plain template literal (NOT cn/twMerge): twMerge treats the custom
          `text-xxs` size utility as conflicting with `text-white` and drops it,
          which breaks selectors/tests that rely on `.text-xxs.text-white`. */}
      <div
        data-testid={`video-variant-label-${realIdx}`}
        className="absolute top-1.5 left-1.5 text-xxs text-white bg-black/60 px-1 rounded z-20"
      >
        {label}
      </div>

      {/* Placeholder cells have no VideoPreviewPlayer, so no overlay cluster to
          join — the star just takes the corner. A cell WITH a clip passes it as
          `topTrailingControls` instead; rendering it here as well would stack
          two stars in the same corner. */}
      {!videoUrl && markerButton && (
        <div className="absolute top-2 right-2 z-30 flex items-center">
          {markerButton}
        </div>
      )}
    </div>
  )
})

VideoHistoryCell.displayName = 'VideoHistoryCell'

export interface VideoVariantsTabContentProps {
  history: VideoHistoryEntry[]
  pickedIndex: number
  onSelectVariant: (index: number) => void
  batchPending: number
  buildVideoUrl: (localPath: string) => string | null
  /** First-frame poster URL builder (RFC-0931); omit → cells keep <video>. */
  buildPosterUrl?: (localPath: string) => string | null
  onDownloadVariant?: (localPath: string, realIdx: number) => void
  /**
   * CSS aspect-ratio (e.g. "9/16") applied when an entry lacks its own
   * persisted `aspect_ratio` metadata. Required so the variant grid never
   * silently falls back to a hardcoded 16/9.
   */
  defaultAspectRatio: string
  /** Used to scope NAD render traces; optional in tests. */
  nodeId?: string
  /**
   * Owning project — required for the RFC-1146 marker API. When absent (tests,
   * or a caller that has not threaded it through) markers are simply inert:
   * the heart renders disabled rather than the component crashing.
   */
  projectId?: string
  /**
   * Whether this tab is currently visible (RFC-1146). Drives the closed→open
   * marker fetch and the open-time snapshot the filter evaluates against.
   */
  isActive?: boolean
  /**
   * When true (default), extreme portrait (9:16) grid aspect is clamped to 3:4 so
   * a row fits in a height-constrained panel. The dedicated Variants panel passes
   * `false` so 9:16 tiles render true-aspect and match the original preview
   * (post 1533007285123236). Must match the value passed to `resolveVideoGridAspect`.
   */
  clampPortrait?: boolean
  /** Incremented by the owning node when automatic navigation should move focus here. */
  focusRequestToken?: number
}

export const VideoVariantsTabContent = memo(function VideoVariantsTabContent({
  history,
  pickedIndex,
  onSelectVariant,
  batchPending,
  buildVideoUrl,
  buildPosterUrl = () => null,
  onDownloadVariant,
  defaultAspectRatio,
  nodeId,
  projectId,
  isActive = true,
  clampPortrait = true,
  focusRequestToken = 0,
}: VideoVariantsTabContentProps) {
  const { t } = useTranslation('canvas')
  const focusTargetRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focusRequestToken <= 0 || !isActive) return
    const frame = requestAnimationFrame(() => focusTargetRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [focusRequestToken, isActive])
  const visibleHistoryEntries = useMemo(
    () => history
      .map((entry, realIdx) => ({ entry, realIdx }))
      .filter(
        (item): item is { entry: NonNullable<VideoHistoryEntry>; realIdx: number } => {
          if (item.entry?.result == null) return false
          // Exclude empty placeholder entries created by out-of-order queued
          // sub-job completion (local_path is "" or null, id="").
          const localPath = item.entry.result.local_path
          return localPath != null && localPath !== ''
        },
      ),
    [history],
  )
  const invalidHistoryCount = history.length - visibleHistoryEntries.length

  const {
    markersLive,
    hasLoaded: markersLoaded,
    setArchived,
  } = useVariantMarkers(projectId, nodeId, isActive)
  const [tab, setTab] = useState<VariantTab>(DEFAULT_VARIANT_TAB)

  const handleSelectTab = useCallback(
    (next: VariantTab) => {
      setTab(next)
      filterDebug({
        event: 'variant-marker:tab-selected',
        trace_id: `variant-marker:${nodeId ?? 'unknown'}`,
        node_id: nodeId ?? null,
        tab: next,
      })
    },
    [nodeId],
  )

  // Ordering deliberately UNCHANGED: newest-first, exactly as before. Markers
  // never reorder the grid — they only govern membership.
  const orderedEntries = useMemo(
    () => [...visibleHistoryEntries].reverse(),
    [visibleHistoryEntries],
  )

  const { visible: filteredEntries, hiddenCount } = useMemo(
    () =>
      // Gated out → no partitioning at all, so the grid is byte-for-byte what
      // it was before RFC-1146.
      selectVariantsForTab(
        orderedEntries,
        (item) => getVariantKey(item.entry.result),
        markersLive,
        tab,
      ),
    [orderedEntries, markersLive, tab],
  )

  // Chip counts read the LIVE map so starring a clip updates its counter
  // immediately, even though tab membership stays snapshot-based.
  const counts = useMemo(
    () =>
      countVariantTabs(
        orderedEntries,
        (item) => getVariantKey(item.entry.result),
        markersLive,
      ),
    [orderedEntries, markersLive],
  )

  // In-flight generations are new, so they are starred by definition and belong
  // to the Variants tab only.
  const pendingInTab = tab === 'variants' ? batchPending : 0
  // Post-partition count — what the grid actually renders.
  const variantCount = filteredEntries.length + pendingInTab
  const totalCount = visibleHistoryEntries.length + batchPending

  useEffect(() => {
    const key = `${nodeId ?? 'unknown'}|${tab}|${filteredEntries.length}|${hiddenCount}`
    if (SEEN_FILTER_KEYS.has(key)) return
    SEEN_FILTER_KEYS.add(key)
    filterDebug({
      event: 'variant-marker:tab-applied',
      node_id: nodeId ?? null,
      tab,
      rendered_count: filteredEntries.length,
      hidden_count: hiddenCount,
      variants_count: counts.variants,
      archived_count: counts.archived,
    })
  }, [nodeId, tab, filteredEntries.length, hiddenCount, counts])

  // Identity diagnostic: marker keys with no matching variant. Deliberately NOT
  // deduped by the module-level set above — a spike means identity drift and
  // must be emitted whenever it changes, not once per (node, tab) shape.
  useEffect(() => {
    const orphans = findOrphanMarkerKeys(
      orderedEntries,
      (item) => getVariantKey(item.entry.result),
      markersLive,
    )
    if (orphans.length > 0) {
      filterDebug({
        event: 'variant-marker:orphan-key',
        trace_id: `variant-marker:${nodeId ?? 'unknown'}`,
        node_id: nodeId ?? null,
        orphan_count: orphans.length,
        local_paths: orphans.slice(0, 5).join(','),
      })
    }
  }, [nodeId, orderedEntries, markersLive])

  useEffect(() => {
    if (!isActive || !markersLoaded) return
    galleryDebug({
      event: 'video-variant-gallery:ui-state-observed',
      trace_id: `video-variant-gallery:${nodeId ?? 'unknown'}`,
      project_id: projectId ?? null,
      node_id: nodeId ?? null,
      selected_variant_index: pickedIndex,
      selected_variant_id: history[pickedIndex]?.result?.id ?? null,
      index_base: 0,
      tab,
      visible_item_count: filteredEntries.length,
      variants_count: counts.variants,
      archived_count: counts.archived,
    })
  }, [isActive, markersLoaded, projectId, nodeId, pickedIndex, history, tab, filteredEntries.length, counts.variants, counts.archived])

  const handleSelectVariant = useCallback((index: number, source: VariantSelectionSource) => {
    galleryDebug({
      event: 'video-variant-gallery:selection-requested',
      trace_id: `video-variant-gallery:${nodeId ?? 'unknown'}`,
      project_id: projectId ?? null,
      node_id: nodeId ?? null,
      previous_variant_index: pickedIndex,
      requested_variant_index: index,
      requested_variant_id: history[index]?.result?.id ?? null,
      index_base: 0,
      interaction_source: source,
      selection_changed: index !== pickedIndex,
      tab,
      visible_item_count: filteredEntries.length,
    })
    onSelectVariant(index)
  }, [onSelectVariant, projectId, nodeId, pickedIndex, history, tab, filteredEntries.length])

  useEffect(() => {
    if (invalidHistoryCount === 0) return
    const key = `${nodeId ?? 'unknown'}|${history.length}|${invalidHistoryCount}|${visibleHistoryEntries.length}`
    if (SEEN_NULL_HISTORY_KEYS.has(key)) return
    SEEN_NULL_HISTORY_KEYS.add(key)
    nullHistoryDebug({
      event: 'video-variant-history:null-slots-skipped',
      trace_id: `video-variant-null-result:${nodeId ?? 'unknown'}`,
      node_id: nodeId ?? null,
      original_length: history.length,
      failed_count: invalidHistoryCount,
      remaining_count: visibleHistoryEntries.length,
    })
  }, [history.length, invalidHistoryCount, nodeId, visibleHistoryEntries.length])

  // Base aspect for the grid:
  //   - Latest variant's aspect when history is non-empty (chronological
  //     last entry — the "current intent" the user just generated).
  //   - The node's defaultAspectRatio when history is empty (e.g. only
  //     pending placeholders).
  // Every cell renders at this aspect; column count adapts to it so a
  // portrait grid shows 3 columns of compact cells instead of 2 columns
  // of cells that scroll forever.
  // Grid base aspect via the shared resolver so the rendered grid and the
  // dedicated-panel layout sizing always agree (post 1533007285123236).
  // `clampPortrait` (legacy shared panel) clamps 9:16 → 3:4; the dedicated
  // Variants panel passes false so tiles match the original preview.
  const rawGridAspect = resolveVideoGridAspect(history, defaultAspectRatio, {
    clampPortrait: false,
  })
  const gridAspect = resolveVideoGridAspect(history, defaultAspectRatio, {
    clampPortrait,
  })
  const rawRatio = parseAspectRatio(rawGridAspect)
  // Portrait base → 3 columns (more cells per row offsets the height
  // each cell takes); landscape/square base → 2 columns (cells already
  // wide and short, fewer columns reads better).
  const columnCount = parseAspectRatio(gridAspect) < 1 ? 3 : 2
  const wasClamped = clampPortrait && gridAspect !== rawGridAspect

  useEffect(() => {
    if (wasClamped) {
      variantDebug({
        event: 'video-variant:portrait-aspect-clamped',
        nodeId: nodeId ?? null,
        rawAspect: rawGridAspect,
        clampedAspect: '3/4',
        rawRatio,
        columnCount,
      })
    }
  }, [wasClamped, rawGridAspect, rawRatio, columnCount, nodeId])

  // Fullscreen lightbox state
  const [fullscreenVideoUrl, setFullscreenVideoUrl] = useState<string | null>(null)
  // Suspend canvas keyboard shortcuts while the fullscreen preview lightbox is
  // open (see canvasShortcutGuard). The docked tab itself does not block.
  useBlockingOverlay(fullscreenVideoUrl !== null)
  const handleExpand = useCallback((url: string) => {
    setFullscreenVideoUrl(url)
  }, [])

  return (
    <>
    <div
      ref={focusTargetRef}
      tabIndex={-1}
      className="h-full p-3 overflow-y-auto nowheel focus-visible:ring-2 focus-visible:ring-primary/50"
      data-testid="video-variants-focus-target"
    >
      {totalCount === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <svg
            className="w-8 h-8 text-text-muted/30 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-xs font-medium text-text-muted">
            {t('videoVariantsTabContent.noVideosYet')}
          </p>
          <p className="text-xxs text-text-muted/60 mt-1">
            {t('videoVariantsTabContent.generateToCreateVariants')}
          </p>
        </div>
      ) : (
        <>
          {/* Markers not in yet: hold the chips AND the grid, so the panel does
              not assert a wrong split and then correct itself. See the image
              tab and VariantsGridSkeleton. */}
          {!markersLoaded ? (
            <VariantsGridSkeleton
              tileCount={totalCount}
              columns={columnCount}
              aspectRatio={gridAspect}
            />
          ) : (
          <>
          {/* Variants / Archived chips. Rendered whenever any variant exists,
              even if the active tab is empty — otherwise the user could not
              switch back to the tab that holds them. */}
          <VariantTabChips
            tab={tab}
            onSelect={handleSelectTab}
            counts={counts}
          />
          {variantCount === 0 ? (
            <div
              className="flex flex-col items-center justify-center text-center py-6"
              data-testid="video-variants-tab-empty-state"
            >
              <p className="text-xs font-medium text-text-muted">
                {t(`variantMarker.tabEmpty.${tab}`, { count: hiddenCount })}
              </p>
            </div>
          ) : (
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            }}
          >
            {/* Pending spinners — render at the grid's base aspect so they
                line up with the rendered cells (uniform shape across the
                grid). Variants tab only; see `pendingInTab`. */}
            {Array.from({ length: pendingInTab }).map(
              (_, i) => {
                return (
                  <div
                    key={`pending-${i}`}
                    data-testid={`video-variant-pending-${i}`}
                    className="rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
                    style={{
                      aspectRatio: gridAspect,
                      width: '100%',
                    }}
                  >
                    <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )
              }
            )}
            {/* History items — newest first, order UNCHANGED by markers. */}
            {filteredEntries
              .map(({ entry, realIdx }) => {
                const cellAspect = getEntryAspect(entry, defaultAspectRatio)
                const variantKey = getVideoVariantKey(entry, realIdx)
                // Marker identity is the asset path, NOT the React key above.
                const markerKey = getVariantKey(entry.result)
                // Star reads the LIVE map (instant feedback); the tab partition
                // above reads the snapshot so the tile does not jump mid-click.
                const archived = isArchivedMarker(
                  markerKey === null ? undefined : markersLive.get(markerKey),
                )
                return (
                  <VideoHistoryCell
                    key={variantKey}
                    archived={archived}
                    onToggleArchived={
                      markerKey === null || !projectId
                        ? undefined
                        : (nextArchived) =>
                            void setArchived(markerKey, nextArchived, {
                              nodeType: 'video_generator',
                              historyIndex: realIdx,
                              variantCountAfter: counts.variants + (nextArchived ? -1 : 1),
                            })
                    }
                    entry={entry}
                    realIdx={realIdx}
                    label={getVariantLabel(history, realIdx)}
                    isSelected={realIdx === pickedIndex}
                    cellAspect={cellAspect}
                    gridAspect={gridAspect}
                    defaultAspect={defaultAspectRatio}
                    nodeId={nodeId}
                    buildVideoUrl={buildVideoUrl}
                    buildPosterUrl={buildPosterUrl}
                    onSelect={handleSelectVariant}
                    onExpand={handleExpand}
                    onDownload={onDownloadVariant}
                  />
                )
              })}
          </div>
          )}
          </>
          )}
        </>
      )}
    </div>

    {/* Fullscreen Video Modal */}
    {fullscreenVideoUrl &&
      createPortal(
        <div
          data-testid="video-fullscreen-modal"
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
          onClick={() => setFullscreenVideoUrl(null)}
        >
          <div
            className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="video-fullscreen-close"
              onClick={() => setFullscreenVideoUrl(null)}
              className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <video
              src={fullscreenVideoUrl}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
})

VideoVariantsTabContent.displayName = 'VideoVariantsTabContent'
